import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import {
  ArrowLeft, TrendingUp, Filter, Search, Building2, MapPin,
  ExternalLink, Copy, FileDown, Compass, Mail, Users, Phone, Linkedin
} from 'lucide-react';
import Footer from '../components/Footer';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVENUE_PRESETS = [
  { label: 'Tutte', min: null, max: null },
  { label: 'Micro (< 500K)', min: null, max: 500000 },
  { label: 'Piccole (500K - 2M)', min: 500000, max: 2000000 },
  { label: 'Medio-Piccole (2M - 10M)', min: 2000000, max: 10000000 },
  { label: 'Medie (10M - 50M)', min: 10000000, max: 50000000 },
  { label: 'Grandi (> 50M)', min: 50000000, max: null },
];

function formatEuro(v) {
  if (v === null || v === undefined) return '—';
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)} mld €`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} mln €`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}K €`;
  return `${v} €`;
}

export default function RevenueSearchPage() {
  const navigate = useNavigate();

  // ==== Batch mode (P.IVA) ====
  const [rawVats, setRawVats] = useState('');
  const [batchMin, setBatchMin] = useState('');
  const [batchMax, setBatchMax] = useState('');
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchResults, setBatchResults] = useState(null);

  const vatsArray = useMemo(() => {
    return Array.from(new Set(
      rawVats.split(/[\s,;\n]+/).map((s) => s.trim().toUpperCase())
        .filter((s) => /^(IT)?\d{6,15}$/.test(s))
    ));
  }, [rawVats]);

  const applyBatchPreset = (p) => {
    setBatchMin(p.min === null ? '' : String(p.min));
    setBatchMax(p.max === null ? '' : String(p.max));
  };

  const handleBatch = async () => {
    if (vatsArray.length === 0) return toast.error('Inserisci almeno una P.IVA valida');
    if (vatsArray.length > 30) return toast.error('Max 30 P.IVA per batch');
    setBatchLoading(true);
    setBatchResults(null);
    try {
      const payload = { vats: vatsArray };
      if (batchMin) payload.min_revenue = parseInt(batchMin, 10);
      if (batchMax) payload.max_revenue = parseInt(batchMax, 10);
      const res = await axios.post(`${API_URL}/revenue/batch`, payload);
      setBatchResults(res.data);
      toast.success(`Trovate ${res.data.matching_filter} aziende su ${res.data.total} P.IVA`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella ricerca');
    } finally {
      setBatchLoading(false);
    }
  };

  // ==== Discover mode (Città + Fascia) ====
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');
  const [discoverMin, setDiscoverMin] = useState('500000');
  const [discoverMax, setDiscoverMax] = useState('5000000');
  const [maxCandidates, setMaxCandidates] = useState(15);
  const [discoverJobId, setDiscoverJobId] = useState(null);
  const [discoverJob, setDiscoverJob] = useState(null);
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const pollRef = useRef(null);

  const applyDiscoverPreset = (p) => {
    setDiscoverMin(p.min === null ? '' : String(p.min));
    setDiscoverMax(p.max === null ? '' : String(p.max));
  };

  const startDiscover = async () => {
    if (!city.trim()) {
      return toast.error('Inserisci almeno la città');
    }
    setDiscoverLoading(true);
    setDiscoverJob(null);
    setDiscoverJobId(null);
    try {
      const payload = {
        category: category.trim(),  // opzionale
        city: city.trim(),
        max_candidates: parseInt(maxCandidates, 10) || 15,
      };
      if (discoverMin) payload.min_revenue = parseInt(discoverMin, 10);
      if (discoverMax) payload.max_revenue = parseInt(discoverMax, 10);
      const res = await axios.post(`${API_URL}/revenue/discover`, payload);
      setDiscoverJobId(res.data.job_id);
      toast.success('Ricerca avviata — analisi in corso (2-5 min)');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella ricerca');
      setDiscoverLoading(false);
    }
  };

  useEffect(() => {
    if (!discoverJobId) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API_URL}/scraper/status/${discoverJobId}`);
        setDiscoverJob(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setDiscoverLoading(false);
          clearInterval(pollRef.current);
          if (res.data.status === 'completed') {
            toast.success(`Analisi completata: ${res.data.matches?.length || 0} match nella fascia`);
          } else {
            toast.error('Analisi fallita: ' + (res.data.error || 'errore sconosciuto'));
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [discoverJobId]);

  const downloadDiscoverCsv = () => {
    const matches = discoverJob?.matches || [];
    if (!matches.length) return;
    const header = 'Azienda,P.IVA,Fatturato,Anno,ATECO,Attività,Indirizzo,Telefono,Sito,Email PEC,Web Emails,Hunter Emails,Decision Makers,LinkedIn';
    const rows = matches.map((m) => [
      `"${(m.name || '').replace(/"/g, '""')}"`,
      m.vat_number || '',
      m.revenue_eur ?? '',
      m.revenue_year ?? '',
      m.ateco || '',
      `"${(m.ateco_desc || '').replace(/"/g, '""')}"`,
      `"${(m.address || '').replace(/"/g, '""')}"`,
      m.phone || '',
      m.website || '',
      m.pec_probable || '',
      `"${(m.web_emails || []).join(';')}"`,
      `"${(m.hunter_emails || []).map((e) => e.email).join(';')}"`,
      `"${(m.decision_makers || []).map((e) => `${e.email} (${e.position || ''})`).join(';')}"`,
      m.linkedin_company_url || m.linkedin_search_url || '',
    ].join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `aziende_${city.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ==== Batch mode helpers ====
  const copyBatchMatches = () => {
    if (!batchResults?.matches?.length) return;
    const text = batchResults.matches
      .map((r) => `${r.company_name}\t${r.vat_number}\t${r.revenue_label}\t${r.ateco || ''}\t${r.address || ''}`)
      .join('\n');
    navigator.clipboard.writeText(text);
    toast.success('Copiato negli appunti');
  };

  const downloadBatchCsv = () => {
    if (!batchResults?.matches?.length) return;
    const header = 'Azienda,P.IVA,Fatturato,Anno,ATECO,Attività,Indirizzo,Forma Giuridica,URL';
    const rows = batchResults.matches.map((r) => [
      `"${(r.company_name || '').replace(/"/g, '""')}"`,
      r.vat_number || '', r.revenue_eur ?? '', r.revenue_year ?? '',
      r.ateco || '', `"${(r.ateco_desc || '').replace(/"/g, '""')}"`,
      `"${(r.address || '').replace(/"/g, '""')}"`,
      `"${(r.legal_form || '').replace(/"/g, '""')}"`,
      r.source_url || '',
    ].join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fatturati_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const discoverMatches = discoverJob?.matches || [];
  const discoverAllResults = discoverJob?.results || [];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Toaster position="top-right" richColors />

      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">Ricerca per Fatturato</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full">
        <Tabs defaultValue="discover" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="mode-tabs">
            <TabsTrigger value="discover" data-testid="tab-discover">
              <Compass className="w-4 h-4 mr-2" /> Scoperta per Città
            </TabsTrigger>
            <TabsTrigger value="batch" data-testid="tab-batch">
              <Filter className="w-4 h-4 mr-2" /> Da lista P.IVA
            </TabsTrigger>
          </TabsList>

          {/* ================= DISCOVER TAB ================= */}
          <TabsContent value="discover">
            <div className="mb-4 rounded-xl bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 px-5 py-4 text-sm text-slate-700 leading-relaxed">
              <strong className="text-emerald-700">Scoperta automatica:</strong> inserisci solo la <strong>città</strong>
              (categoria facoltativa) e il sistema trova le aziende su Google Maps, estrae P.IVA dai siti,
              recupera <strong>fatturato ufficiale</strong>, filtra per la tua fascia e arricchisce con
              <strong> email, persone, decision makers</strong> (Hunter.io).
              Tempo stimato: <strong>2-5 minuti</strong> per 15 candidati.
            </div>

            <Card className="mb-6" data-testid="discover-form">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Compass className="w-5 h-5 text-emerald-600" />
                  Nuova Scoperta
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="d-city" className="text-sm font-medium">Città <span className="text-red-500">*</span></Label>
                    <Input
                      id="d-city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="es. Milano, Roma, Bergamo"
                      className="mt-1.5"
                      data-testid="discover-city-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="d-category" className="text-sm font-medium">
                      Categoria / Settore <span className="text-slate-400 font-normal">(facoltativo)</span>
                    </Label>
                    <Input
                      id="d-category"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      placeholder="lascia vuoto per trovare tutte le aziende"
                      className="mt-1.5"
                      data-testid="discover-category-input"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Fascia fatturato (preset)</Label>
                  <div className="flex flex-wrap gap-2">
                    {REVENUE_PRESETS.map((p) => (
                      <button
                        key={p.label}
                        type="button"
                        onClick={() => applyDiscoverPreset(p)}
                        className="text-xs px-3 py-1.5 rounded-full border border-slate-300 bg-white hover:bg-emerald-50 hover:border-emerald-400 transition-colors"
                        data-testid={`discover-preset-${p.label}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="d-min" className="text-sm font-medium">Fatturato min (€)</Label>
                    <Input id="d-min" type="number" value={discoverMin} onChange={(e) => setDiscoverMin(e.target.value)}
                      placeholder="500000" className="mt-1.5" data-testid="discover-min-input" />
                  </div>
                  <div>
                    <Label htmlFor="d-max" className="text-sm font-medium">Fatturato max (€)</Label>
                    <Input id="d-max" type="number" value={discoverMax} onChange={(e) => setDiscoverMax(e.target.value)}
                      placeholder="5000000" className="mt-1.5" data-testid="discover-max-input" />
                  </div>
                  <div>
                    <Label htmlFor="d-cand" className="text-sm font-medium">Aziende da analizzare</Label>
                    <Input id="d-cand" type="number" min={1} max={30} value={maxCandidates}
                      onChange={(e) => setMaxCandidates(e.target.value)}
                      className="mt-1.5" data-testid="discover-max-candidates-input" />
                  </div>
                </div>

                <Button
                  onClick={startDiscover}
                  disabled={discoverLoading || !city.trim()}
                  className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="discover-submit-btn"
                >
                  {discoverLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                      Analisi in corso...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      Avvia Scoperta
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Progress + Results */}
            {discoverJob && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="discover-results">
                <Card className="mb-4">
                  <CardContent className="p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-xs text-slate-500 mb-1">Stato ricerca</div>
                        <div className="text-sm font-medium text-slate-900">
                          {discoverJob.current_task || 'In corso...'}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Badge className={discoverJob.status === 'completed' ? 'bg-emerald-600' : discoverJob.status === 'failed' ? 'bg-red-600' : 'bg-blue-600'}>
                          {discoverJob.status}
                        </Badge>
                        <Badge variant="outline">Candidati: {discoverJob.candidates_scanned || 0}</Badge>
                        <Badge variant="outline">P.IVA trovate: {discoverJob.vats_found || 0}</Badge>
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                          Match: {discoverMatches.length}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {discoverMatches.length > 0 && (
                  <div className="flex justify-end mb-3">
                    <Button variant="outline" size="sm" onClick={downloadDiscoverCsv} data-testid="discover-csv-btn">
                      <FileDown className="w-4 h-4 mr-2" /> Scarica CSV completo
                    </Button>
                  </div>
                )}

                <div className="grid gap-4">
                  {discoverMatches.map((m, idx) => (
                    <Card key={idx} className="hover:shadow-md transition-shadow" data-testid={`discover-match-${idx}`}>
                      <CardContent className="p-5">
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Building2 className="w-4 h-4 text-slate-500 shrink-0" />
                              <h3 className="font-semibold text-slate-900">{m.name}</h3>
                            </div>
                            {m.vat_number && (
                              <div className="text-xs text-slate-500 font-mono mb-1">P.IVA {m.vat_number}</div>
                            )}
                            {m.address && (
                              <div className="flex items-center gap-1.5 text-sm text-slate-600 mb-1">
                                <MapPin className="w-3.5 h-3.5" /> {m.address}
                              </div>
                            )}
                            {m.phone && (
                              <div className="flex items-center gap-1.5 text-sm text-slate-600 mb-1">
                                <Phone className="w-3.5 h-3.5" /> <a href={`tel:${m.phone}`} className="hover:underline">{m.phone}</a>
                              </div>
                            )}
                            {m.website && (
                              <div className="flex items-center gap-1.5 text-sm text-emerald-600 mb-1 truncate">
                                <ExternalLink className="w-3.5 h-3.5" />
                                <a href={m.website} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">
                                  {m.website}
                                </a>
                              </div>
                            )}
                            {m.ateco && (
                              <div className="text-xs text-slate-600 mt-2">
                                <span className="inline-block px-2 py-0.5 bg-slate-100 rounded font-mono mr-2">{m.ateco}</span>
                                {m.ateco_desc}
                              </div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-2xl font-bold text-emerald-600">{m.revenue_label || formatEuro(m.revenue_eur)}</div>
                            {m.revenue_year && <div className="text-xs text-slate-500">Esercizio {m.revenue_year}</div>}
                            {m.legal_form && <div className="text-xs text-slate-500 mt-1">{m.legal_form}</div>}
                          </div>
                        </div>

                        {/* Contatti arricchiti */}
                        {(m.decision_makers?.length > 0 || m.hunter_emails?.length > 0 || m.web_emails?.length > 0 || m.pec_probable) && (
                          <div className="border-t pt-3 mt-3 space-y-2">
                            {m.decision_makers?.length > 0 && (
                              <div>
                                <div className="text-xs font-semibold text-purple-700 mb-1 flex items-center gap-1">
                                  <Users className="w-3.5 h-3.5" /> Decision Makers ({m.decision_makers.length})
                                </div>
                                <div className="grid gap-1.5">
                                  {m.decision_makers.slice(0, 5).map((dm, i) => (
                                    <div key={i} className="flex flex-wrap items-center gap-2 text-sm bg-purple-50 rounded px-2 py-1">
                                      <span className="font-medium">{dm.first_name} {dm.last_name}</span>
                                      {dm.position && <Badge variant="outline" className="text-xs">{dm.position}</Badge>}
                                      <a href={`mailto:${dm.email}`} className="text-purple-700 hover:underline text-xs">{dm.email}</a>
                                      {dm.linkedin && (
                                        <a href={dm.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                                          <Linkedin className="w-3.5 h-3.5" />
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {m.hunter_emails?.length > 0 && m.hunter_emails.length > (m.decision_makers?.length || 0) && (
                              <div>
                                <div className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1">
                                  <Mail className="w-3.5 h-3.5" /> Altri contatti Hunter ({m.hunter_emails.length - (m.decision_makers?.length || 0)})
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {m.hunter_emails
                                    .filter((e) => !(m.decision_makers || []).some((dm) => dm.email === e.email))
                                    .slice(0, 5)
                                    .map((e, i) => (
                                      <a key={i} href={`mailto:${e.email}`} className="text-xs bg-blue-50 text-blue-700 rounded px-2 py-1 hover:bg-blue-100">
                                        {e.email}{e.position ? ` · ${e.position}` : ''}
                                      </a>
                                    ))}
                                </div>
                              </div>
                            )}

                            {m.web_emails?.length > 0 && (
                              <div>
                                <div className="text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1">
                                  <Mail className="w-3.5 h-3.5" /> Email dal sito
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                  {m.web_emails.slice(0, 6).map((e, i) => (
                                    <a key={i} href={`mailto:${e}`} className="text-xs bg-slate-100 text-slate-700 rounded px-2 py-1 hover:bg-slate-200">
                                      {e}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}

                            {m.pec_probable && (
                              <div className="text-xs text-slate-600">
                                <span className="font-semibold">PEC probabile:</span> <a href={`mailto:${m.pec_probable}`} className="hover:underline">{m.pec_probable}</a>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Non-matches */}
                {discoverAllResults.length > 0 && discoverAllResults.length > discoverMatches.length && (
                  <details className="mt-6">
                    <summary className="text-sm text-slate-600 cursor-pointer hover:text-slate-900" data-testid="discover-excluded-toggle">
                      Mostra {discoverAllResults.length - discoverMatches.length} aziende scartate
                    </summary>
                    <div className="mt-3 grid gap-2">
                      {discoverAllResults.filter((r) => !r.in_range).map((r, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-100 rounded-lg text-sm">
                          <div className="min-w-0 flex-1">
                            <span className="font-medium truncate">{r.name}</span>
                            {r.vat_number && <span className="ml-2 text-xs text-slate-500 font-mono">P.IVA {r.vat_number}</span>}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {r.revenue_label && <span className="text-slate-600 text-xs">{r.revenue_label}</span>}
                            <Badge variant="outline" className="text-xs">
                              {r.revenue_status === 'vat_not_found' && 'P.IVA non trovata'}
                              {r.revenue_status === 'revenue_not_found' && 'Fatturato N/D'}
                              {r.revenue_status === 'no_website' && 'Sito assente'}
                              {r.revenue_status === 'revenue_error' && 'Errore fetch'}
                              {r.revenue_status === 'found' && 'Fuori fascia'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </motion.div>
            )}
          </TabsContent>

          {/* ================= BATCH TAB ================= */}
          <TabsContent value="batch">
            <div className="mb-4 rounded-xl bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 px-5 py-4 text-sm text-slate-700 leading-relaxed">
              <strong className="text-orange-700">Fonte gratuita:</strong> <em>fatturatoaziende.it</em>.
              Inserisci fino a <strong>30 P.IVA</strong>. Vengono estratti: fatturato, ATECO, forma giuridica, sede, REA.
            </div>

            <Card className="mb-6" data-testid="revenue-search-form">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-orange-600" /> Nuova Ricerca
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <Label htmlFor="vats" className="text-sm font-medium">
                    Partite IVA <span className="text-slate-500">({vatsArray.length} valide)</span>
                  </Label>
                  <Textarea id="vats" value={rawVats} onChange={(e) => setRawVats(e.target.value)}
                    placeholder="03803010168&#10;00159560366&#10;IT12345678901" rows={6}
                    className="mt-1.5 font-mono text-sm" data-testid="vats-textarea" />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Fascia fatturato (preset)</Label>
                  <div className="flex flex-wrap gap-2">
                    {REVENUE_PRESETS.map((p) => (
                      <button key={p.label} type="button" onClick={() => applyBatchPreset(p)}
                        className="text-xs px-3 py-1.5 rounded-full border border-slate-300 bg-white hover:bg-orange-50 hover:border-orange-400 transition-colors"
                        data-testid={`preset-${p.label}`}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="min-rev" className="text-sm font-medium">Fatturato minimo (€)</Label>
                    <Input id="min-rev" type="number" value={batchMin} onChange={(e) => setBatchMin(e.target.value)}
                      placeholder="500000" className="mt-1.5" data-testid="min-revenue-input" />
                  </div>
                  <div>
                    <Label htmlFor="max-rev" className="text-sm font-medium">Fatturato massimo (€)</Label>
                    <Input id="max-rev" type="number" value={batchMax} onChange={(e) => setBatchMax(e.target.value)}
                      placeholder="10000000" className="mt-1.5" data-testid="max-revenue-input" />
                  </div>
                </div>

                <Button onClick={handleBatch} disabled={batchLoading || vatsArray.length === 0}
                  className="w-full h-11 bg-orange-600 hover:bg-orange-700 text-white" data-testid="submit-search-btn">
                  {batchLoading ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />Ricerca in corso...</>
                  ) : (
                    <><Filter className="w-4 h-4 mr-2" />Cerca Fatturato</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {batchResults && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="results-section">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-emerald-600 text-white text-sm px-3 py-1">{batchResults.matching_filter} match</Badge>
                    <Badge variant="outline" className="text-sm">{batchResults.found} trovate su {batchResults.total}</Badge>
                  </div>
                  {batchResults.matches.length > 0 && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={copyBatchMatches} data-testid="copy-results-btn">
                        <Copy className="w-4 h-4 mr-2" /> Copia
                      </Button>
                      <Button variant="outline" size="sm" onClick={downloadBatchCsv} data-testid="download-csv-btn">
                        <FileDown className="w-4 h-4 mr-2" /> CSV
                      </Button>
                    </div>
                  )}
                </div>

                {batchResults.matches.length > 0 ? (
                  <div className="grid gap-3">
                    {batchResults.matches.map((r, idx) => (
                      <Card key={idx} className="hover:shadow-md transition-shadow" data-testid={`match-card-${idx}`}>
                        <CardContent className="p-5">
                          <div className="flex flex-wrap items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Building2 className="w-4 h-4 text-slate-500 shrink-0" />
                                <h3 className="font-semibold text-slate-900 truncate">{r.company_name || '—'}</h3>
                              </div>
                              <div className="text-xs text-slate-500 space-x-3 mb-2 font-mono">
                                <span>P.IVA {r.vat_number}</span>
                                {r.rea && <span>REA {r.rea}</span>}
                              </div>
                              {r.address && (
                                <div className="flex items-center gap-1.5 text-sm text-slate-600 mb-2">
                                  <MapPin className="w-3.5 h-3.5" /> {r.address}
                                </div>
                              )}
                              {r.ateco && (
                                <div className="text-xs text-slate-600 mb-1">
                                  <span className="inline-block px-2 py-0.5 bg-slate-100 rounded font-mono mr-2">{r.ateco}</span>
                                  {r.ateco_desc}
                                </div>
                              )}
                              {r.legal_form && <div className="text-xs text-slate-500 mt-1">{r.legal_form}</div>}
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-2xl font-bold text-orange-600">{r.revenue_label || formatEuro(r.revenue_eur)}</div>
                              {r.revenue_year && <div className="text-xs text-slate-500">Esercizio {r.revenue_year}</div>}
                              {r.source_url && (
                                <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-xs text-orange-600 hover:underline mt-2">
                                  <ExternalLink className="w-3 h-3" /> scheda completa
                                </a>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center text-slate-500">
                      Nessuna azienda trovata nella fascia di fatturato selezionata.
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Footer />
    </div>
  );
}
